import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import TextInputFieldWithValidation from '../../ValidationFormElements/TextInputFieldWithValidation';
import useDeviceResize from '../../../hooks/useDeviceResize';
import EditableInputAdornment from '../EditableInputAdornment';
import { setPayerContactInfo } from '../../../redux/modules/payment';
import { setDataFormValues, triggerDataValidations } from '../../../helpers/formInputHelpers';
import useRegisterValidationFields from '../../../hooks/useRegisterValidationFields';
import useOnClickOutside from '../../../hooks/useOnClickOutside';

const PayerContactInfoForm = ({ formProps }) => {
  const { register, setValue, triggerValidation, errors, formState } = formProps;
  const dispatch = useDispatch();
  const phoneRef = useRef();
  const emailRef = useRef();
  const isDeviceMdSize = useDeviceResize('md');
  const { signer } = useSelector((store) => store.recPayment.contract, shallowEqual);

  const { payerContactInfo } = useSelector(
    (store) => store.paymentReducer,
    shallowEqual,
  );
  const { isInsured, isEmailDefault } = payerContactInfo;

  const [isPhoneEditable, setIsPhoneEditable] = useState(false);
  const [isEmailEditable, setIsEmailEditable] = useState(false);

  const isPhoneDisabled = useMemo(() => isInsured
      && !isPhoneEditable, [isInsured, isPhoneEditable]);

  const isEmailDisabled = useMemo(() => isInsured
      && !isEmailEditable, [isInsured, isEmailEditable]);

  useRegisterValidationFields([
    'firstName',
    'lastName',
    'email',
    'emailConfirm',
    'mobilePhone',
    'isInsured',
    'isPhoneDefault',
    'isEmailDefault',
  ], register);

  useEffect(() => {
    if (isInsured) {
      setIsEmailEditable(false);
      setIsPhoneEditable(false);
    }
  }, [isInsured]);

  useEffect(() => {
    if (payerContactInfo.firstName) {
      setDataFormValues(payerContactInfo, setValue);
    }
  }, [payerContactInfo, setValue, formState]);

  const setInput = useCallback(
    (key, value) => {
      setValue(key, value);
      dispatch(setPayerContactInfo({ [key]: value }));
    },
    [dispatch, setValue],
  );

  const handleInputChange = useCallback(
    (e) => {
      const { name: key, value } = e.target;
      setInput(key, value);
    },
    [setInput],
  );

  useEffect(() => {
    if (isInsured) {
      const keys = ['firstName', 'lastName', 'mobilePhone', 'email', 'emailConfirm'];
      triggerDataValidations(keys, errors, triggerValidation);
    }
  }, [errors, isInsured, triggerValidation]);

  const handlePhoneCheck = () => {
    setInput('isPhoneDefault', false);
    triggerValidation({ name: 'mobilePhone' }).then(
      (isValid) => {
        setIsPhoneEditable(!isValid);
      },
    );
  };

  const handleEmailCheck = () => {
    setInput('isEmailDefault', false);
    Promise.allSettled([
      triggerValidation({ name: 'email' }),
      triggerValidation({ name: 'emailConfirm' }),
    ]).then((results) => {
      const areFieldsValid = results.every(
        (result) => result.status === 'fulfilled' && result.value,
      );
      setIsEmailEditable(!areFieldsValid);
    });
  };

  useOnClickOutside(phoneRef, () => {
    if (isPhoneEditable) {
      handlePhoneCheck();
    }
  });

  useOnClickOutside(emailRef, () => {
    if (isEmailEditable) {
      handleEmailCheck();
    }
  });

  return (
    <form>
      <Grid container spacing={4}>
        {!isInsured && (
        <>
          <Grid item md={6} xs={12}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="recurringPayment.payerContactInfo.name"
              name="firstName"
              autoComplete="off"
              value={payerContactInfo.firstName}
              handleChange={(e) => handleInputChange(e)}
              disabled={isInsured}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="recurringPayment.payerContactInfo.lastName"
              name="lastName"
              autoComplete="off"
              value={payerContactInfo.lastName}
              handleChange={(e) => handleInputChange(e)}
              disabled={isInsured}
            />
          </Grid>
        </>
        )}
        <Grid item md={6} xs={12} ref={phoneRef}>
          <TextInputFieldWithValidation
            type="tel"
            required
            formProps={formProps}
            labelKey="recurringPayment.payerContactInfo.mobile"
            name="mobilePhone"
            autoComplete="off"
            onPaste={(e) => e.preventDefault()}
            placeholder="093xxxxxx"
            value={payerContactInfo.mobilePhone}
            handleChange={(e) => handleInputChange(e)}
            disabled={isPhoneDisabled}
            InputProps={{
              endAdornment: isInsured && (
              <EditableInputAdornment
                isDisabled={isPhoneDisabled}
                setIsEditable={setIsPhoneEditable}
                setIsDefault={(val) => setInput('isPhoneDefault', val)}
                handleReset={() => setInput('mobilePhone', '')}
                handleCheck={handlePhoneCheck}
                handleCancel={() => {
                  setInput('mobilePhone', signer.mobilePhone);
                  triggerValidation({ name: 'mobilePhone' });
                }}
              />
              ),
            }}
          />
        </Grid>
        <Grid item md={6} xs={12} ref={emailRef}>
          <TextInputFieldWithValidation
            required
            formProps={formProps}
            labelKey="recurringPayment.payerContactInfo.mail"
            name="email"
            autoComplete="off"
            value={payerContactInfo.email}
            handleChange={(e) => handleInputChange(e)}
            disabled={isEmailDisabled}
            InputProps={{
              endAdornment: isInsured && (
              <EditableInputAdornment
                isDisabled={isEmailDisabled}
                setIsEditable={setIsEmailEditable}
                setIsDefault={(val) => setInput('isEmailDefault', val)}
                handleReset={() => {
                  setInput('email', '');
                  setInput('emailConfirm', '');
                }}
                handleCheck={handleEmailCheck}
                handleCancel={() => {
                  setInput('email', signer.email);
                  triggerValidation({ name: 'email' });
                }}
              />
              ),
            }}
          />
        </Grid>

        {(!isInsured || isEmailEditable || !isEmailDefault) && (
        <>
          {!isDeviceMdSize && <Grid item md={6} xs={12} /> }
          <Grid item md={6} xs={12}>
            <TextInputFieldWithValidation
              disabled={isEmailDisabled}
              required
              formProps={formProps}
              labelKey="recurringPayment.payerContactInfo.confirmMail"
              name="emailConfirm"
              autoComplete="off"
              onPaste={(e) => e.preventDefault()}
              value={payerContactInfo.emailConfirm}
              handleChange={(e) => handleInputChange(e)}
            />
          </Grid>
        </>
        )}
      </Grid>
    </form>
  );
};

PayerContactInfoForm.propTypes = {
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    errors: PropTypes.shape({
      firstName: PropTypes.shape({}),
      lastName: PropTypes.shape({}),
      mobilePhone: PropTypes.shape({}),
      email: PropTypes.shape({}),
      emailConfirm: PropTypes.shape({}),
    }),
    clearError: PropTypes.func,
    triggerValidation: PropTypes.func,
    formState: PropTypes.shape({
      dirty: PropTypes.bool,
    }),
  }).isRequired,
};

export default PayerContactInfoForm;
